import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, FlaskConical, ShieldCheck, Globe, Activity, Brain, Zap } from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const floatCardVariants = {
  hidden: { opacity: 0, scale: 0.9, y: 16 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const heroFloatCards = [
  { label: 'Tissue repair', icon: Activity },
  { label: 'Energy', icon: Zap },
  { label: 'Cognitive', icon: Brain },
  { label: 'Metabolic boost', icon: FlaskConical },
];

export default function HeroSection() {
  return (
    <section
      className="relative overflow-hidden bg-gradient-to-br from-rp-hero-from via-rp-hero-via to-rp-hero-to"
    >
      <div className="rp-container relative z-10 grid items-center gap-12 py-20 md:grid-cols-2 md:gap-16 md:py-28 lg:py-36">
        {/* Left: copy */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="flex flex-col items-start"
        >
          <motion.span
            variants={itemVariants}
            className="rp-mono-mini text-rp-moss"
          >
            Reference-grade research peptides
          </motion.span>

          <motion.h1
            variants={itemVariants}
            className="rp-headline mt-6 max-w-[15ch] text-display-hero text-rp-ink text-balance"
          >
            The future of <em className="font-medium italic text-rp-forest">research</em>
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="mt-6 max-w-lg text-base leading-relaxed text-rp-moss md:text-lg"
          >
            UAE-sourced lyophilized peptides with full documentation, batch traceability, and research-use compliance. Every vial ships with a COA and HPLC certificate.
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="mt-6 flex flex-wrap items-center gap-4 text-xs text-rp-moss"
          >
            <span className="inline-flex items-center gap-1.5 font-mono uppercase tracking-wider">
              <ShieldCheck size={14} className="text-rp-forest" />
              COA & Batch Verified
            </span>
            <span className="inline-flex items-center gap-1.5 font-mono uppercase tracking-wider">
              <Globe size={14} className="text-rp-forest" />
              UAE-Sourced · Research Use
            </span>
          </motion.div>

          <motion.div variants={itemVariants} className="mt-9 flex flex-wrap items-center gap-3">
            <Link
              to="/products"
              className="rp-btn-primary"
            >
              Shop now
              <ArrowRight size={14} />
            </Link>
            <Link
              to="/quiz"
              className="rp-btn-outline"
            >
              Take the quiz
            </Link>
          </motion.div>
        </motion.div>

        {/* Right: visual */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
          className="relative flex items-center justify-center"
        >
          <motion.div
            variants={itemVariants}
            className="rp-hero-pen-shadow relative w-[280px] sm:w-[320px] lg:w-[380px]"
          >
            <img
              src="/products/product-mockups/pen-v2.png"
              alt="AURA pre-filled multi-dose peptide pen"
              className="h-auto w-full"
            />
          </motion.div>

          {/* Floating cards */}
          {heroFloatCards.map((card, index) => {
            const positions = [
              'left-0 top-[10%] -translate-x-3 sm:-translate-x-8',
              'right-0 top-[26%] translate-x-3 sm:translate-x-8',
              'left-0 bottom-[26%] -translate-x-3 sm:-translate-x-8',
              'right-0 bottom-[10%] translate-x-3 sm:translate-x-8',
            ];
            const delays = [0.2, 0.35, 0.5, 0.65];
            const Icon = card.icon;
            return (
              <motion.div
                key={card.label}
                variants={floatCardVariants}
                custom={index}
                transition={{ delay: delays[index] }}
                whileHover={{ y: -4, scale: 1.03 }}
                className={`absolute ${positions[index]} inline-flex items-center gap-2 rounded-full border border-rp-line bg-rp-paper/95 px-3 py-2 shadow-card-hover backdrop-blur-sm sm:px-4 sm:py-2.5`}
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-rp-leaf-soft text-rp-forest">
                  <Icon size={12} strokeWidth={2.5} />
                </span>
                <span className="whitespace-nowrap font-mono text-[10px] font-bold uppercase tracking-wider text-rp-ink sm:text-xs">
                  {card.label}
                </span>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
